import React, { useState, useEffect, useRef } from "react";
import ThemeToggle from "@/components/ThemeToggle";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { api, formatApiError } from "@/lib/apiClient";
import { Music2, ArrowRight, Mail } from "lucide-react";

// Live registered-artist count, refreshed every 60s.
function LiveArtistCount() {
  const [n, setN] = useState(null);
  useEffect(() => {
    let alive = true;
    const pull = () => api.get("/public/artist-count").then(({ data }) => alive && setN(data.count)).catch(() => {});
    pull();
    const t = setInterval(() => { if (!document.hidden) pull(); }, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  return <span data-testid="auth-live-artist-count">{n == null ? "…" : `${n.toLocaleString()}+`}</span>;
}
import BrandLogo from "@/components/BrandLogo";

const SPLIT_BG = "https://images.unsplash.com/photo-1684162205807-630e5d5904be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMHNvdW5kJTIwd2F2ZSUyMGRhcmt8ZW58MHx8fHwxNzgwNTExOTk1fDA&ixlib=rb-4.1.0&q=85";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthPage() {
  const [params] = useSearchParams();
  // Only same-site paths are allowed as a post-login redirect.
  const redirectTo = (params.get("redirect") || "").startsWith("/") && !(params.get("redirect") || "").startsWith("//") ? params.get("redirect") : null;
  const [mode, setMode] = useState(params.get("signup") === "1" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingVerify, setPendingVerify] = useState(null);
  const [code, setCode] = useState("");
  const { login, register, setUser, refresh } = useAuth();
  const [termsOk, setTermsOk] = useState(false);
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const googleBtnRef = useRef(null);

  // Auth page is always rendered on dark theme — preserve user's previous choice and restore on unmount.
  useEffect(() => {
    const prev = theme;
    if (prev !== "dark") setTheme("dark");
    return () => {
      if (prev && prev !== "dark") setTheme(prev);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load Google Identity Services script once and render the official Google button
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const ensureScript = () => new Promise((resolve) => {
      if (window.google?.accounts?.id) return resolve();
      if (document.getElementById("gis-script")) {
        document.getElementById("gis-script").addEventListener("load", resolve);
        return;
      }
      const s = document.createElement("script");
      s.src = "https://accounts.google.com/gsi/client";
      s.async = true; s.defer = true; s.id = "gis-script";
      s.onload = resolve;
      document.head.appendChild(s);
    });

    let cancelled = false;
    ensureScript().then(() => {
      if (cancelled || !window.google?.accounts?.id || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async ({ credential }) => {
          try {
            const { data } = await api.post("/auth/google/token", { credential });
            setUser(data);
            toast.success(`Welcome, ${data.name}`);
            navigate(data.role === "admin" ? "/admin" : (redirectTo || "/dashboard"));
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Google sign-in failed");
          }
        },
        ux_mode: "popup",
        auto_select: false,
      });
      googleBtnRef.current.innerHTML = "";
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: "filled_black", size: "large", shape: "pill",
        text: mode === "register" ? "signup_with" : "signin_with",
        logo_alignment: "left", width: 360,
      });
    });
    return () => { cancelled = true; };
  }, [mode, navigate, setUser]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        const u = await login(email, password);
        toast.success(`Welcome back, ${u.name?.split(" ")[0] || "artist"}`);
        navigate(u.role === "admin" ? "/admin" : (redirectTo || "/dashboard"));
      } else {
        if (!termsOk) { toast.error("Please tick 'I accept the Terms and Conditions' to continue."); setBusy(false); return; }
        const { data } = await api.post("/auth/register", { email, password, name, terms_accepted: true });
        setUser(data);
        setPendingVerify({ email: data.email, devCode: data.dev_verification_code, emailSent: data.email_sent });
        toast.success(data.email_sent ? "Verification code sent to your email" : "Verification code generated");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-email", { code });
      toast.success(`Email verified — welcome, ${data?.name || "artist"} 🎉`);
      await refresh();
      navigate(redirectTo || "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const resendCode = async () => {
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data.dev_verification_code) {
        setPendingVerify((p) => ({ ...p, devCode: data.dev_verification_code, emailSent: false }));
      } else {
        setPendingVerify((p) => ({ ...p, emailSent: data.email_sent }));
      }
      toast.success("New code generated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  // === Email verification screen ===
  if (pendingVerify) {
    return (
      <div className="min-h-screen flex items-center justify-center auth-page text-zinc-100 p-6 grain relative" data-testid="auth-verify-root">
        <form onSubmit={verifyCode} className="w-full max-w-md space-y-6" data-testid="verify-form">
          <div className="flex items-center gap-2">
            <BrandLogo size={28} />
          </div>
          <div>
            <div className="w-12 h-12 rounded-full bg-[#FF4500]/10 text-[#FF4500] flex items-center justify-center mb-4"><Mail size={20} /></div>
            <h1 className="font-display text-3xl tracking-tight">Verify your email</h1>
            <p className="text-sm text-zinc-500 mt-1">We sent a 6-digit code to <span className="text-zinc-300">{pendingVerify.email}</span>. Enter it below to activate your account.</p>
          </div>

          {pendingVerify.devCode && process.env.NODE_ENV === "development" && (
            <div className="border border-[#FF4500]/30 bg-[#FF4500]/5 rounded-lg p-4" data-testid="verify-dev-code">
              <div className="text-[10px] uppercase tracking-wider text-[#FF4500]">Dev mode</div>
              <p className="text-zinc-300 text-sm mt-1">Code:</p>
              <div className="font-display text-3xl tracking-[0.5em] text-[#FF4500] mt-2">{pendingVerify.devCode}</div>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Verification code</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required maxLength={6} inputMode="numeric"
              className="bg-[#0F0F11] border-zinc-800 h-12 rounded-md focus-visible:ring-[#FF4500] text-center text-2xl tracking-[0.5em] font-display"
              data-testid="verify-input-code" placeholder="000000" />
          </div>

          <Button type="submit" disabled={busy || code.length !== 6}
            className="w-full bg-[#FF4500] hover:bg-[#FF6A33] text-black font-semibold rounded-full h-11 dm-accent-glow"
            data-testid="verify-submit-btn">
            {busy ? "Verifying…" : "Verify & continue"} <ArrowRight size={16} className="ml-2" />
          </Button>

          <div className="flex items-center justify-between text-sm text-zinc-500">
            <button type="button" onClick={resendCode} className="hover:text-[#FF4500]" data-testid="verify-resend-btn">Resend code</button>
            <button type="button" onClick={() => { setPendingVerify(null); setMode("login"); }} className="hover:text-[#FF4500]" data-testid="verify-cancel-btn">Use another account</button>
          </div>
        </form>
      </div>
    );
  }

  // === Login / Register screen ===
  return (
    <div className="min-h-screen grid lg:grid-cols-2 auth-page text-zinc-100 relative" data-testid="auth-root">
      <div className="hidden lg:block relative overflow-hidden bg-black auth-hero" data-testid="auth-hero">
        {/* Live background image with Ken Burns + breathing zoom */}
        <img src={SPLIT_BG} alt="" className="absolute inset-0 w-full h-full object-cover auth-bg-img" />
        {/* Color veil to fuse with brand — heavier dark wash to neutralise the photo's natural tints. */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/65 to-[#FF4500]/30" />
        {/* Drifting brand-color orbs */}
        <div className="auth-orb auth-orb-1" aria-hidden="true" />
        <div className="auth-orb auth-orb-2" aria-hidden="true" />
        {/* Rising particles */}
        <div className="auth-particles" aria-hidden="true">
          {Array.from({ length: 18 }).map((_, i) => (
            <span key={i} className="auth-particle" style={{ "--i": i, "--x": `${(i * 53) % 100}%`, "--d": `${8 + (i % 6) * 2}s`, "--dl": `${(i * 0.4) % 6}s` }} />
          ))}
        </div>

        <div className="relative h-full flex flex-col justify-between p-12 z-10">
          <Link to="/" className="flex items-center gap-2" data-testid="auth-logo">
            <BrandLogo size={28} />
          </Link>
          <div className="max-w-md">
            <div className="inline-flex items-center gap-2 text-[11px] text-zinc-100 border border-white/15 rounded-full px-3 py-1 mb-6 backdrop-blur bg-white/5">
              <span className="text-[#FF4500] text-sm leading-none" aria-hidden="true">✦</span> AI-first music distribution
            </div>
            <h2 className="font-display text-5xl tracking-tight leading-[1.05] text-white">
              Distribute your AI music to <span className="gradient-text-accent">every DSP.</span>
            </h2>
            <p className="text-sm text-zinc-200/90 mt-5 leading-relaxed">
              Distribute your <strong className="text-white">Suno · Udio</strong> AI-generated music simply. 100% royalties. Auto AI-disclosure. 150+ DSPs in 24 hours.
            </p>
            <div className="mt-6 flex items-center gap-3 text-[11px] text-zinc-300">
              <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> <LiveArtistCount /> artists distributing today</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-5 py-8 sm:p-8 lg:p-16 grain auth-right-panel">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="auth-form">
          <div className="lg:hidden flex items-center justify-between gap-2 mb-2">
            <BrandLogo size={28} />
            <ThemeToggle />
          </div>
          <div>
            <h1 className="font-display text-3xl tracking-tight">{mode === "login" ? "Welcome back" : "Join DistributeMusic.AI"}</h1>
            <p className="text-sm text-zinc-400 mt-1">{mode === "login" ? "Sign in to your dashboard." : "Distribute your first AI track in under 5 minutes."}</p>
          </div>

          {/* Official Google button (rendered by Google Identity Services) */}
          <div ref={googleBtnRef} className="flex justify-center min-h-[44px]" data-testid="auth-google-btn-container" />

          <div className="flex items-center gap-3 text-xs text-zinc-400">
            <div className="h-px bg-gradient-to-r from-transparent via-[#FF4500]/40 to-transparent flex-1" /> OR <div className="h-px bg-gradient-to-r from-transparent via-[#FF4500]/40 to-transparent flex-1" />
          </div>

          {mode === "register" && (
            <div className="space-y-2">
              <Label className="text-[#FF4500]/80 text-xs uppercase tracking-wider">Artist name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required
                className="bg-[#0F0F11] border-[#FF4500]/25 hover:border-[#FF4500]/50 focus:border-[#FF4500] h-11 rounded-md focus-visible:ring-[#FF4500] transition-colors"
                data-testid="auth-input-name" placeholder="Aurora Vox" />
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-[#FF4500]/80 text-xs uppercase tracking-wider">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="bg-[#0F0F11] border-[#FF4500]/25 hover:border-[#FF4500]/50 focus:border-[#FF4500] h-11 rounded-md focus-visible:ring-[#FF4500] transition-colors"
              data-testid="auth-input-email" placeholder="you@studio.com" />
          </div>
          <div className="space-y-2">
            <Label className="text-[#FF4500]/80 text-xs uppercase tracking-wider">Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
              className="bg-[#0F0F11] border-[#FF4500]/25 hover:border-[#FF4500]/50 focus:border-[#FF4500] h-11 rounded-md focus-visible:ring-[#FF4500] transition-colors"
              data-testid="auth-input-password" placeholder="••••••••" />
          </div>

          {mode === "register" && (
            <label data-testid="auth-terms-label"
              className={`dm-terms group flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer select-none transition-[border-color,background-color,box-shadow] duration-300 ${termsOk ? "border-[#FF4500]/70 bg-[#FF4500]/[0.07] shadow-[0_0_22px_-6px_rgba(255,69,0,0.55)]" : "border-white/10 bg-white/[0.02] hover:border-[#FF4500]/40"}`}>
              <input type="checkbox" checked={termsOk} onChange={(e) => setTermsOk(e.target.checked)} required
                className="peer sr-only" data-testid="auth-terms-checkbox" />
              <span aria-hidden="true"
                className={`relative mt-0.5 shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-[background-color,border-color,transform] duration-300 peer-focus-visible:ring-2 peer-focus-visible:ring-[#FF4500]/60 ${termsOk ? "bg-[#FF4500] border-[#FF4500] scale-110" : "border-zinc-500 group-hover:border-[#FF4500]/70"}`}>
                <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="#000" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 8.5l3.2 3L13 4.5" pathLength="1"
                    style={{ strokeDasharray: 1, strokeDashoffset: termsOk ? 0 : 1, transition: "stroke-dashoffset 300ms ease 80ms" }} />
                </svg>
              </span>
              <span className="text-sm leading-relaxed text-zinc-400">
                I accept the <Link to="/terms" target="_blank" onClick={(e) => e.stopPropagation()} className="text-[#FF4500] font-medium hover:underline">Terms and Conditions</Link> and <Link to="/privacy" target="_blank" onClick={(e) => e.stopPropagation()} className="text-[#FF4500] font-medium hover:underline">Privacy Policy</Link>.
                <span className={`block text-xs mt-1 transition-colors duration-300 ${termsOk ? "text-[#FF4500]" : "text-zinc-500"}`} data-testid="auth-terms-status">
                  {termsOk ? "Thanks! You're all set to create your account." : "Tick the box to continue."}
                </span>
              </span>
            </label>
          )}
          <Button type="submit" disabled={busy || (mode === "register" && !termsOk)}
            className="w-full bg-[#FF4500] hover:bg-[#FF6A33] text-black font-semibold rounded-full h-11 dm-accent-glow"
            data-testid="auth-submit-btn">
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"} <ArrowRight size={16} className="ml-2" />
          </Button>

          {mode === "login" && (
            <div className="text-center -mt-2">
              <Link to="/forgot-password" className="text-xs text-zinc-400 hover:text-[#FF4500] transition-colors" data-testid="auth-forgot-link">
                Forgot your password?
              </Link>
            </div>
          )}

          <div className="text-sm text-zinc-400 text-center">
            {mode === "login" ? (
              <>New artist? <button type="button" onClick={() => setMode("register")} className="text-[#FF4500] hover:underline" data-testid="auth-switch-register">Create an account</button></>
            ) : (
              <>Already on DistributeMusic? <button type="button" onClick={() => setMode("login")} className="text-[#FF4500] hover:underline" data-testid="auth-switch-login">Sign in</button></>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
